import React, { useEffect, useState, useMemo } from "react";
import { getActivities } from "../api/activityApi";
import { getTasks } from "../api/taskapi";
import {
  FiEye,
  FiEdit,
  FiPlusCircle,
  FiX,
  FiPlus,
  FiChevronUp,
  FiChevronDown,
  FiLoader,
} from "react-icons/fi";
import { toast } from "react-toastify";
import AddTaskModal from "../pages/programManagerDashboard/AddTaskModal";
import ActivityFormModal from "./AddActivityModal";


// --- ActivitySummaryModal Component ---
const ActivitySummaryModal = ({ activity, onClose }) => {
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [errorTasks, setErrorTasks] = useState(null);

  useEffect(() => {
    const fetchTasks = async () => {
      if (!activity || !activity._id) return;

      setLoadingTasks(true);
      setErrorTasks(null);

      try {
        const allTasks = await getTasks();
        const activityTasks = allTasks.filter(task =>
          task.activity && (task.activity._id === activity._id || task.activity === activity._id)
        );
        setTasks(activityTasks);
      } catch (err) {
        console.error("Error fetching tasks:", err);
        setErrorTasks("Failed to load tasks for this activity.");
      } finally {
        setLoadingTasks(false);
      }
    };

    fetchTasks();
  }, [activity]);

  if (!activity) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen">
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>
        <div className="bg-white rounded-lg shadow-xl transform transition-all max-w-lg w-full p-6 relative z-10">
          <div className="flex justify-between items-center pb-3 border-b mb-4">
            <h3 className="text-2xl font-semibold text-gray-800">
              Activity Summary
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FiX size={24} />
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500">Activity Title</p>
              <h4 className="text-lg font-medium text-gray-900">
                {activity.activityTitle}
              </h4>
            </div>
            <div>
              <p className="text-sm text-gray-500">Belongs to Project</p>
              <h4 className="text-lg font-medium text-gray-900">
                {activity.projectId?.projectTitle || "N/A"}
              </h4>
            </div>
            <div>
              <p className="text-sm text-gray-500">Due Date</p>
              <p className="text-lg font-medium text-gray-900">
                {new Date(activity.dueDate).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <p className="text-lg font-medium text-gray-900">
                {activity.status}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Associated Tasks</p>
              {loadingTasks ? (
                <div className="flex items-center mt-2">
                  <FiLoader className="animate-spin text-orange-500 mr-2" />
                  <span className="text-gray-600">Loading tasks...</span>
                </div>
              ) : errorTasks ? (
                <p className="text-red-500 mt-2">{errorTasks}</p>
              ) : tasks && tasks.length > 0 ? (
                <ul className="list-disc list-inside mt-2 space-y-1">
                  {tasks.map((task) => (
                    <li key={task?._id} className="text-gray-700">
                      {task?.title || "Unnamed Task"}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 mt-2">
                  No tasks associated with this activity.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Custom Hook for Activities Data Fetching ---
const useActivitiesData = (projects) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchActivities = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const allActivities = await getActivities();

      // Get project IDs that belong to the current user
      const userProjectIds = projects.map(project => project._id);

      // Filter activities to only show those that belong to the user's projects
      const filteredActivities = allActivities.filter(activity => {
        const activityProjectId = activity.projectId?._id || activity.projectId;
        return userProjectIds.includes(activityProjectId);
      });

      setActivities(filteredActivities);
    } catch (err) {
      console.error("Error fetching activities:", err.message);
      setError("Failed to fetch activities.");
      toast.error("Failed to fetch activities.");
    } finally {
      setLoading(false);
    }
  }, [projects]);

  useEffect(() => {
    if (projects && projects.length > 0) {
      fetchActivities();
    } else if (projects && projects.length === 0) {
      // If projects array is empty but defined, set loading to false
      setActivities([]);
      setLoading(false);
    }
  }, [projects, fetchActivities]);

  return { activities, loading, error, refresh: fetchActivities };
};

// --- AllActivitiesTable Component ---
const AllActivitiesTable = ({ searchQuery, projects = [], currentUser }) => {
  const { activities, loading, error, refresh } = useActivitiesData(projects);

  const [selectedProject, setSelectedProject] = useState("");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const [currentPage, setCurrentPage] = useState(1);
  const activitiesPerPage = 10;

  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showActivityFormModal, setShowActivityFormModal] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  const getStatusStyle = (status) => {
    switch (status) {
      case "in_progress":
        return "text-blue-600 bg-blue-100 px-2 py-1 rounded-full text-sm font-medium";
      case "pending":
        return "text-yellow-600 bg-yellow-100 px-2 py-1 rounded-full text-sm font-medium";
      case "completed":
        return "text-green-600 bg-green-100 px-2 py-1 rounded-full text-sm font-medium";
      default:
        return "text-gray-600";
    }
  };

  // Memoize filtered and sorted activities for performance
  const processedActivities = useMemo(() => {
    // 1. Filter activities
    let currentFilteredActivities = activities.filter((activity) => {
      if (!activity) {
        return false;
      }
      const matchesSearch = searchQuery
        ? activity.activityTitle
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        : true;
      const activityProjectId = activity.projectId?._id || activity.projectId;
      const matchesProject =
        selectedProject === "" || activityProjectId === selectedProject;
      return matchesSearch && matchesProject;
    });

    // 2. Sort activities
    if (sortColumn) {
      currentFilteredActivities.sort((a, b) => {
        let aValue = a[sortColumn];
        let bValue = b[sortColumn];

        if (sortColumn === "dueDate") {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        } else if (sortColumn === "projectId") {
          aValue = a.projectId?.projectTitle || "";
          bValue = b.projectId?.projectTitle || "";
        }

        if (aValue < bValue) {
          return sortDirection === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortDirection === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return currentFilteredActivities;
  }, [activities, searchQuery, selectedProject, sortColumn, sortDirection]);

  // Pagination logic
  const indexOfLastActivity = currentPage * activitiesPerPage;
  const indexOfFirstActivity = indexOfLastActivity - activitiesPerPage;
  const currentActivities = processedActivities.slice(
    indexOfFirstActivity,
    indexOfLastActivity
  );
  const totalPages = Math.ceil(processedActivities.length / activitiesPerPage);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const handleViewActivity = (activity) => {
    setSelectedActivity(activity);
    setShowSummaryModal(true);
  };

  const handleEditActivity = (activity) => {
    setSelectedActivity(activity);
    setShowActivityFormModal(true);
  };

  const handleAddActivity = () => {
    if (!selectedProject) {
      toast.info("Please select a project before adding a new activity.");
      return;
    }
    setSelectedActivity(null);
    setShowActivityFormModal(true);
  };

  const handleCreateTask = (activity) => {
    setSelectedActivity(activity);
    setShowAddTaskModal(true);
  };

  const handleModalClose = () => {
    setShowActivityFormModal(false);
    setShowSummaryModal(false);
    setShowAddTaskModal(false);
    setSelectedActivity(null);
    refresh();
  };

  return (
    <div className="mt-6 bg-white p-6 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-4 border-b pb-2">
        <h2 className="text-2xl font-semibold text-gray-800">My Activities</h2>
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <label htmlFor="project-filter" className="mr-2 text-gray-700 font-medium">
              Filter by Project:
            </label>
            <select
              id="project-filter"
              className="border border-gray-300 rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={selectedProject}
              onChange={(e) => {
                setSelectedProject(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All My Projects</option>
              {projects?.length > 0 &&
                projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.projectTitle}
                  </option>
                ))}
            </select>
          </div>

          <button
            onClick={handleAddActivity}
            disabled={!selectedProject}
            className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors
              ${
                !selectedProject
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-orange-600 text-white hover:bg-orange-700"
              }`}
            aria-label="Add a new activity"
            title={
              !selectedProject
                ? "Please select a project to add an activity"
                : "Add a new activity"
            }
          >
            <FiPlusCircle />
            <span>Add Activity</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 flex flex-col items-center">
          <FiLoader className="animate-spin text-orange-500 text-4xl mb-4" />
          <p className="text-gray-500 text-lg">Loading activities...</p>
        </div>
      ) : error ? (
        <div className="text-center text-red-500 py-8">Error: {error}</div>
      ) : processedActivities.length === 0 ? (
        <div className="text-center py-20 flex flex-col items-center">
          <p className="text-gray-500 text-lg mb-4">
            No activities found for this filter.
          </p>
          {!selectedProject ? (
            <p className="text-gray-500 text-md">
              Please select a project from the dropdown to view or add
              activities.
            </p>
          ) : (
            <p className="text-gray-500 text-md">
              Click 'Add Activity' above to add one to the selected project.
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-orange-100 text-gray-600 text-left">
                <th
                  className="px-4 text-orange-600 py-2 cursor-pointer hover:bg-orange-200"
                  onClick={() => handleSort("activityTitle")}
                >
                  Activity Title
                  {sortColumn === "activityTitle" &&
                    (sortDirection === "asc" ? (
                      <FiChevronUp className="inline ml-1" />
                    ) : (
                      <FiChevronDown className="inline ml-1" />
                    ))}
                </th>
                <th
                  className="px-4 text-orange-600 py-2 cursor-pointer hover:bg-orange-200"
                  onClick={() => handleSort("dueDate")}
                >
                  Due Date
                  {sortColumn === "dueDate" &&
                    (sortDirection === "asc" ? (
                      <FiChevronUp className="inline ml-1" />
                    ) : (
                      <FiChevronDown className="inline ml-1" />
                    ))}
                </th>
                <th
                  className="px-4 text-orange-600 py-2 cursor-pointer hover:bg-orange-200"
                  onClick={() => handleSort("status")}
                >
                  Status
                  {sortColumn === "status" &&
                    (sortDirection === "asc" ? (
                      <FiChevronUp className="inline ml-1" />
                    ) : (
                      <FiChevronDown className="inline ml-1" />
                    ))}
                </th>
                <th className="px-4 text-orange-600 py-2 text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {currentActivities.map((activity) => (
                <tr
                  key={activity?._id || Math.random()}
                  className="border-b hover:bg-gray-50 transition-colors"
                >
                  <td className="py-2 px-4">{activity.activityTitle}</td>
                  <td className="py-2 px-4">
                    {new Date(activity.dueDate).toLocaleDateString()}
                  </td>
                  <td className="py-2 px-4">
                    <span className={getStatusStyle(activity.status)}>
                      {activity.status}
                    </span>
                  </td>
                  <td className="py-2 px-4">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleViewActivity(activity)}
                        className="text-gray-500 hover:text-green-600 p-1 rounded-full transition-colors"
                        title="View Details"
                        aria-label={`View details for ${activity.activityTitle}`}
                      >
                        <FiEye size={18} />
                      </button>
                      <button
                        onClick={() => handleEditActivity(activity)}
                        className="text-gray-500 hover:text-blue-600 p-1 rounded-full transition-colors"
                        title="Edit Activity"
                        aria-label={`Edit ${activity.activityTitle}`}
                      >
                        <FiEdit size={18} />
                      </button>
                      <button
                        onClick={() => handleCreateTask(activity)}
                        className="text-white bg-orange-300 px-3 py-1 rounded-lg flex items-center gap-1 hover:bg-orange-700 transition-colors"
                        title="Add Task"
                        aria-label={`Add a new task to ${activity.activityTitle}`}
                      >
                        <FiPlus size={16} />
                        <span className="text-sm">Add Task</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {processedActivities.length > activitiesPerPage && (
            <div className="flex justify-center mt-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className={`rounded px-3 py-1 ${
                    currentPage === 1
                      ? "cursor-not-allowed bg-gray-300"
                      : "bg-orange-100 text-orange-600 hover:bg-orange-200"
                  }`}
                >
                  Prev
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`rounded px-3 py-1 ${
                      currentPage === i + 1
                        ? "bg-orange-300 text-orange-900 font-semibold"
                        : "bg-gray-200 hover:bg-gray-300"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className={`rounded px-3 py-1 ${
                    currentPage === totalPages
                      ? "cursor-not-allowed bg-gray-300"
                      : "bg-orange-100 text-orange-600 hover:bg-orange-200"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {showSummaryModal && (
        <ActivitySummaryModal activity={selectedActivity} onClose={handleModalClose} />
      )}
      {showActivityFormModal && (
        <ActivityFormModal
          activity={selectedActivity}
          projectId={selectedProject}
          onClose={handleModalClose}
          onActivitySaved={refresh}
          currentUser={currentUser}
        />
      )}
      {showAddTaskModal && selectedActivity && (
        <AddTaskModal
          isOpen={showAddTaskModal}
          onClose={handleModalClose}
          activityId={selectedActivity._id}
          onTaskCreated={refresh}
        />
      )}
    </div>
  );
};

export default AllActivitiesTable;